import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import useColumns from '@harbor/elements/utils/table/hooks/columns';
import TableStore from '@harbor/elements/utils/table/store';
import ApiService from '../../../../../../../config/api-config';
import { downloadAsCSVFile } from '../../../../../../../utils/common';
import Spinner from '../../../../../../../common/Spinner';
import CustomTimeStamp from '../../../../../../../generics/TimeStamp/customTimeStamp';
import { adTopClientsPayload } from '../../../../../../../config/reqConfig/anomalyDetection';
import ADTableView from '../../../genericComponents/ADTableView';
import { addUnitsFromDate } from '../../../../../../../utils/displayTime';
import columnConfig from './topClientTableConfig';
import css from '../../../common/anomalyMagneticStyle.less';
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import ErrorComponent from '../../../../../../../common/ErrorComponent';
import { payloadOnQueryUpdate } from '../../../common/adUtils';
import useI18n from '../../../../../../../i18n/useI18n';
import { AD_USAGE_TOP_CLIENTS_TABLE_DEFAULT_TGL, LOCAL_STORAGE_LABELS } from '../../../../../../../utils/enums';
import { useTableSettings } from '../../../../../../../utils/table';

const defaultSort = [['usage', 'desc']];
const tableSettingsId = "ad_usage_top_clients_table";

const [ HbrBadge ] = reactWrapper(["hbr-badge"]);

const TopClients = (props) => {
    const { tViews } = useI18n('views');
    const latestRequest = useRef(0);
    const tableStoreRef = useRef(null);
    const [tableLoader, setTableLoader] = useState(null);
    const [tableError, setTableError] = useState(null);
    const [tableStore, setTableStore] = useState(null);

    const openTableSettings = useCallback(() => {
        tableStoreRef.current?.setState({ settingsOpen: true });
    }, []);

    const columnsHook = useColumns(useMemo(() => ({
        config: () => columnConfig({ openTableSettings }),
        order: defaultSort
    }), [openTableSettings]));

    useEffect(() => {
        const abortController = new AbortController();
        latestRequest.current += 1;
        const requestId = latestRequest.current;
        setTableError(null);
        try {
            
            if(props.cuiProps.circuitName){
                let tableStore = new TableStore({
                query: { pageLength : 30 },
                hooks: [columnsHook],
                fetchData: async ({ query, setSource, setCount }) => {
                    if(!query.export) setTableLoader(true);
                    let payload = adTopClientsPayload(props.cuiProps.circuitName, props.selectedHour);
                    payload = payloadOnQueryUpdate(query, payload);
                    let res = await ApiService.getAdTopClients(payload, { signal: abortController.signal });
                    if (latestRequest.current !== requestId) return;
                    if (!res.errorObject && !query.export) {
                        setSource(res?.data?.data);
                        setCount(res?.data?.count, false);
                        setTableLoader(false);
                    } else if(query.export) {
                        let resData = res?.data?.data;
                        let columns = tableStore.getState().columns
                            .filter(item => item.prop !== "actions");
                            
                        const data = [columns.map(item => `"${item.name}"`).join(",")];
                        for (const item of resData) {
                            const rowData = columns.map(col => {
                                const value = item[col.prop] ?? '';
                                return `"${value}"`;
                            });
                            data.push(rowData.join(","));
                        }
                        downloadAsCSVFile(data.join("\n"), tViews('userTableHeadingTopClients'));
                        delete query.export;
                        setTableLoader(false);
                    } else {
                        setTableError(res.errorObject);
                        setTableLoader(false);
                    }
                }
            });
            tableStoreRef.current = tableStore;
            useTableSettings(
                LOCAL_STORAGE_LABELS.AD_USAGE_TOP_CLIENTS_TABLE,
                AD_USAGE_TOP_CLIENTS_TABLE_DEFAULT_TGL,
                columnsHook,
                tableStore
            );
            setTableStore(tableStore);
        }
        } catch (error) {
            // Handle error
            if (error.name === "AbortError") return; // Ignore abort error
            // Only process if this is the latest request
            if (latestRequest.current !== requestId) return;
            setTableError(error);
            setTableLoader(false);
        }
    },[props.cuiProps.circuitName]);

    return (
        <div className={css["ad-top-clients"]}
            aria-label={tViews('usageBasedAD.clientsByUsage')}>
            <h2>{tViews('usageBasedAD.clientsByUsage')}{tViews('postSpace')}
                <HbrBadge slot="suffix" pill size="small" aria-hidden="true" data-testid="usage-based-topclient-circuit-badge">
                    {`${tViews('usageBasedAD.Circuit')}: ${props.cuiProps.displayCircuitName}`}
                </HbrBadge>
            </h2>
            {tableStore
                ? <div>
                        <div className="customt-timestamp">
                            <CustomTimeStamp
                                prevTime={props.selectedHour}
                                currentTime={addUnitsFromDate(new Date(props.selectedHour), 1, "h")} />
                        </div>
                        <ADTableView
                            tableStore={tableStore}
                                columns={tableStore?.getState()?.columns}
                                id={"ad-top-client-table"}
                                status={{ isLoading: tableLoader, error: tableError }}
                                defaultSort={defaultSort}
                                isPagination={true}
                                entity={tViews('topClients')}
                                tableSettings={{
                                    localStorageName: LOCAL_STORAGE_LABELS.AD_USAGE_TOP_CLIENTS_TABLE,
                                    defaultToggles: AD_USAGE_TOP_CLIENTS_TABLE_DEFAULT_TGL,
                                    dataTestid: tableSettingsId
                                }}/>
                        </div>
                : tableLoader 
                    ? <Spinner />
                    : tableError && (
                        <ErrorComponent
                            code={"500"}
                            width={"110px"}
                            className={"large-dashlet-error"}/>
                    )}
        </div>
    )
}

TopClients.propTypes = {
    cuiProps: PropTypes.object.isRequired,
    site: PropTypes.number.isRequired,
    selectedHour: PropTypes.string,
};

export default TopClients;

